import React, { ReactNode } from 'react';

import {
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import { GRID_DETAIL_PANEL_TOGGLE_FIELD } from '@mui/x-data-grid-pro';

import { Title } from '../../../@types/Calculations';
import { Province } from '../../../@types/KingdomResponse';
import { letterize } from '../../../helpers/NumberFormatting';
import CustomDetailPanelToggle from '../components/CustomDetailPanelToggle';
import {
  getAllIntelAge,
  getBadSpells,
  getGoodSpells,
  getArmy,
  getIntelAgeWithToolTip,
  getBuildingColName,
  simpleComparator,
  getCombo,
  getSciPercentage,
  getSurveyRenderValue,
  getRequests,
  getSurveyValue,
  getSciValue,
  colorCodedResult,
  getColorizedDefHomeWithTooltip,
  colorCodedLetterizedResult,
  percentageComparator,
  getGoodShortSpellNames,
  getBadShortSpellNames,
  getFirstArmySecondsFromParam,
  getObjectives,
  ranks,
} from '../helpers/colHelpers';

export type RenderCellParams = GridRenderCellParams<any, Province, ReactNode>;
export type ValueGetterParams = GridValueGetterParams<Province>;

export const mandatoryCols: GridColDef[] = [
  { field: 'id', headerName: 'id', maxWidth: 0, hideable: true },
  { field: 'location', headerName: 'Location', maxWidth: 5, hideable: true },
  {
    field: 'slot',
    headerName: '#',
    minWidth: 30,
    maxWidth: 40,
    hideable: true,
    resizable: true,
  },
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 100,
  },
  {
    field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
    headerName: '',
    headerClassName: () => 'toggle-col',
    cellClassName: () => 'toggle-col',
    renderCell: (params) => <CustomDetailPanelToggle {...params} />,
  },
  {
    field: 'ruler',
    headerName: 'Ruler',
    cellClassName: () => 'smallFont',
    valueGetter: (params: ValueGetterParams) => params.row.sot?.ruler,
  },
  {
    field: 'race',
    headerName: 'Race',
    cellClassName: () => 'smallFont',
    valueGetter: (params: ValueGetterParams) => params.row.race,
  },
  {
    field: 'personality',
    headerName: 'Personality',
    cellClassName: () => 'smallFont',
    valueGetter: (params: ValueGetterParams) => params.row.sot?.personality,
  },
  {
    field: 'combo',
    headerName: 'Combo',
    minWidth: 70,
    valueGetter: (params: ValueGetterParams) => getCombo(params.row),
  },
  {
    field: 'note',
    headerName: 'Note',
    cellClassName: () => 'smallFont',
    description: 'This column is set using OPs planner or Users (Own KD)',
    valueGetter: (params: ValueGetterParams) => params.row.note,
  },
  {
    field: 'group',
    headerName: 'Group',
    cellClassName: () => 'smallFont',
    description: 'This column is set using OPs planner or Users (Own KD)',
    valueGetter: (params: ValueGetterParams) => params.row.group,
  },
  {
    field: 'honor',
    headerName: 'Honor',
    cellClassName: () => 'smallFont',
    valueGetter: (params: ValueGetterParams) =>
      params.row.title ? ranks[params.row.title as Title] : 0,
    renderCell: (params: RenderCellParams) => params.row.title,
  },
  {
    field: 'land',
    headerName: 'Acres',
    align: 'right',
    headerAlign: 'left',
    type: 'number',
    valueGetter: (params: ValueGetterParams) =>
      params.row.sot?.land ?? params.row.land,
  },
  {
    field: 'nw',
    headerName: 'NW',
    align: 'right',
    headerAlign: 'left',
    type: 'number',
    valueGetter: (params: ValueGetterParams) =>
      params.row.sot?.networth ?? params.row.networth,
    renderCell: (params: RenderCellParams) => (
      <div style={{ textTransform: 'lowercase' }}>
        {letterize(params.row.sot?.networth ?? params.row.networth)}
      </div>
    ),
    sortComparator: simpleComparator,
  },
  {
    field: 'nwpa',
    headerName: 'NWpa',
    align: 'right',
    headerAlign: 'left',
    type: 'number',
    valueGetter: (params: ValueGetterParams) =>
      params.row.sot?.nwpa ?? params.row.nwpa,
  },
];
export const map: GridColDef = {
  field: 'map',
  headerName: 'MAP',
  cellClassName: () => 'smallFont',
  description: 'Multi-attack protection taken from SoT',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.map,
  renderCell: (params: RenderCellParams) =>
    colorCodedResult(params.row.sot?.map, params.row.sot?.ageSeconds),
};
export const tpa: GridColDef = {
  field: 'tpa',
  headerName: 'Tpa',
  description: 'Tpa calculated from Sot, or using most recent infiltrate',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.tpa,
  renderCell: (params: RenderCellParams) =>
    colorCodedResult(
      params.row.sot?.tpa,
      params.row.sot?.infilAgeSeconds ?? params.row.sot?.ageSeconds
    ),
};
export const wpa: GridColDef = {
  field: 'wpa',
  headerName: 'Wpa',
  description:
    'Wpa calculated from wizards, or estimated on enemy from SoT, SoS and survey',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.wpa,
  renderCell: (params: RenderCellParams) =>
    colorCodedResult(params.row.sot?.wpa, params.row.sot?.ageSeconds),
};
export const stealth: GridColDef = {
  field: 'stealth',
  headerName: 'Stlth',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.stealth ?? 0,
  renderCell: (params: RenderCellParams) =>
    colorCodedResult(
      params.row.sot?.stealth ? `${params.row.sot.stealth}%` : null,
      params.row.sot?.ageSeconds
    ),
};
export const mana: GridColDef = {
  field: 'mana',
  headerName: 'Mana',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.mana ?? 0,
  renderCell: (params: RenderCellParams) =>
    colorCodedResult(
      params.row.sot?.mana ? `${params.row.sot.mana}%` : null,
      params.row.sot?.ageSeconds
    ),
};
export const ovP: GridColDef = {
  field: 'ovP',
  headerName: 'Pop%',
  description: 'Calculated overpop from Sot, Survey, SoS',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.calcs?.oPop ?? null,
  sortComparator: simpleComparator,
  renderCell: (params: RenderCellParams) =>
    colorCodedResult(
      params.row.calcs?.oPop ? `${params.row.calcs.oPop}%` : null,
      Math.max(
        params.row.sot?.ageSeconds,
        params.row.som?.ageSeconds,
        params.row.survey?.ageSeconds
      )
    ),
};
export const army: GridColDef = {
  field: 'army',
  headerName: 'Army',
  align: 'center',
  cellClassName: () => 'smallFont',
  valueGetter: (params: ValueGetterParams) =>
    getFirstArmySecondsFromParam(params),
  renderCell: (params: RenderCellParams) => getArmy(params),
};
export const off: GridColDef = {
  field: 'off',
  headerName: 'Off',
  description: 'Total offense from SoT',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.offPoints ?? 0,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.offPoints,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const def: GridColDef = {
  field: 'def',
  headerName: 'Def',
  description: 'Total defense from SoT',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.defPoints ?? 0,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.defPoints,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const offHome: GridColDef = {
  field: 'offHome',
  headerName: 'OffHome',
  description: 'Offense home, taken from SoM',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) =>
    params.row.som?.offPointsHome ?? 0,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.som?.offPointsHome,
      params.row.som?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const defHome: GridColDef = {
  field: 'defHome',
  headerName: 'DefHome',
  description: 'Most recent reliable defense using SoD, SoM, SoT',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) =>
    params.row.calcs?.defPointsSummary?.defPointsHome,
  renderCell: (params: RenderCellParams) =>
    getColorizedDefHomeWithTooltip(params),
  sortComparator: simpleComparator,
};
export const somDef: GridColDef = {
  field: 'somDef',
  headerName: 'SomDef',
  description: 'Most recent SoM defense home',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) =>
    params.row.som?.defPointsHome ?? 0,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.som?.defPointsHome,
      params.row.som?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const osPoints: GridColDef = {
  field: 'osPoints',
  headerName: 'OS Off',
  description: 'OffSpecs on horses total offense',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) =>
    params.row.calcs?.mountedOffSpecsRaw ?? null,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.calcs?.mountedOffSpecsRaw ?? null,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const dsPoints: GridColDef = {
  field: 'dsPoints',
  headerName: 'DS Def',
  description: 'DefSpecs total defense',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) =>
    params.row.calcs?.defSpecsRaw ?? null,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.calcs?.defSpecsRaw ?? null,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const opa: GridColDef = {
  field: 'opa',
  headerName: 'Opa',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.opa ?? null,
  renderCell: (params: RenderCellParams) =>
    colorCodedResult(params.row.sot?.opa ?? null, params.row.sot?.ageSeconds),
};
export const dpa: GridColDef = {
  field: 'dpa',
  headerName: 'Dpa',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.dpa ?? null,
  renderCell: (params: RenderCellParams) =>
    colorCodedResult(params.row.sot?.dpa ?? null, params.row.sot?.ageSeconds),
};
export const ome: GridColDef = {
  field: 'ome',
  headerName: 'OME',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) =>
    params.row.som?.ome ? `${params.row.som?.ome}%` : null,
  renderCell: (params: RenderCellParams) =>
    colorCodedResult(
      params.row.som?.ome ? `${params.row.som?.ome}%` : null,
      params.row.som?.ageSeconds
    ),
};
export const dme: GridColDef = {
  field: 'dme',
  headerName: 'DME',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) =>
    params.row.som?.dme ? `${params.row.som?.dme}%` : null,
  renderCell: (params: RenderCellParams) =>
    colorCodedResult(
      params.row.som?.dme ? `${params.row.som?.dme}%` : null,
      params.row.som?.ageSeconds
    ),
};
export const peons: GridColDef = {
  field: 'peons',
  headerName: 'Peons',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.peasants,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.peasants,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const horses: GridColDef = {
  field: 'horses',
  headerName: 'Horses',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.horses,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.horses,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const solds: GridColDef = {
  field: 'solds',
  headerName: 'Solds',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.soldiers,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.soldiers,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const prisoners: GridColDef = {
  field: 'prisoners',
  headerName: 'Prisoners',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.prisoners,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.prisoners,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const ppa: GridColDef = {
  field: 'ppa',
  headerName: 'Ppa',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.ppa ?? null,
  renderCell: (params: RenderCellParams) =>
    colorCodedResult(params.row.sot?.ppa ?? null, params.row.sot?.ageSeconds),
};
export const nonPeon: GridColDef = {
  field: 'nonPeon',
  headerName: 'NonPeon',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) =>
    params.row.som?.nonPeon ? `${params.row.som.nonPeon}%` : null,
  renderCell: (params: RenderCellParams) =>
    colorCodedResult(
      params.row.som?.nonPeon ? `${params.row.som.nonPeon}%` : null,
      params.row.som?.ageSeconds
    ),
};
export const thieves: GridColDef = {
  field: 'thieves',
  headerName: 'Thv',
  description: 'Number of thieves from SoT or infiltrate',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.thieves,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.thieves,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const wizards: GridColDef = {
  field: 'wizards',
  headerName: 'Wiz',
  description:
    'Number of wizards from SoT or estimated on enemey using SoT, SoS and Survey',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.wizards,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.wizards,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const ospecs: GridColDef = {
  field: 'ospecs',
  headerName: 'OS',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.oSpecs,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.oSpecs,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const dspecs: GridColDef = {
  field: 'dspecs',
  headerName: 'DS',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.dSpecs,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.dSpecs,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const leets: GridColDef = {
  field: 'leets',
  headerName: 'Leets',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.elites,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.elites,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const ospecsTraining: GridColDef = {
  field: 'osTraining',
  headerName: 'OS Train',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.som?.training.oSpecs,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.som?.training.oSpecs,
      params.row.som?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const dspecsTraining: GridColDef = {
  field: 'dsTraining',
  headerName: 'DS Train',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.som?.training.dSpecs,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.som?.training.dSpecs,
      params.row.som?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const eliteTraining: GridColDef = {
  field: 'eliteTraining',
  headerName: 'Elite Train',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.som?.training.elites,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.som?.training.elites,
      params.row.som?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const thievesTraining: GridColDef = {
  field: 'thiefTraining',
  headerName: 'Thief Train',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.som?.training.thieves,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.som?.training.thieves,
      params.row.som?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const military: GridColDef = {
  field: 'military',
  headerName: 'Military',
  description: 'Total troops not including wizards or thieves',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) =>
    params.row.sot?.totalTroops ?? null,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.totalTroops ?? null,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const be: GridColDef = {
  field: 'be',
  headerName: 'BE',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) =>
    params.row.sot ? `${params.row.sot?.be}%` : null,
  renderCell: (params: RenderCellParams) =>
    colorCodedResult(
      params.row.sot ? `${params.row.sot?.be}%` : null,
      params.row.sot?.ageSeconds
    ),
  sortComparator: percentageComparator,
};
export const wages: GridColDef = {
  field: 'wages',
  headerName: 'Wages',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.ma?.wages,
  renderCell: (params: RenderCellParams) =>
    colorCodedResult(
      params.row.ma?.wages ? `${params.row.ma?.wages}%` : '',
      params.row.som?.ageSeconds
    ),
};
export const draftTarget: GridColDef = {
  field: 'draftTarget',
  headerName: 'DrftTgt',
  align: 'center',
  headerAlign: 'left',
  valueGetter: (params: ValueGetterParams) =>
    params.row.ma?.draftTarget ? `${params.row.ma.draftTarget}%` : null,
  renderCell: (params: RenderCellParams) =>
    colorCodedResult(
      params.row.ma?.draftTarget ? `${params.row.ma.draftTarget}%` : null,
      params.row.som?.ageSeconds
    ),
};
export const draftRate: GridColDef = {
  field: 'draftRate',
  headerName: 'DrftRate',
  align: 'left',
  valueGetter: (params: ValueGetterParams) =>
    params.row.ma?.draftRate ? params.row.ma.draftRate : '',
  renderCell: (params: RenderCellParams) =>
    colorCodedResult(
      params.row.ma?.draftRate ? params.row.ma.draftRate : '',
      params.row.som?.ageSeconds
    ),
};
export const requests: GridColDef = {
  field: 'requests',
  description:
    'This column is set using OPs planner and shows the required ops to be performed. Duration spell requests are hidden automatically once the spell is active.',
  headerName: 'OP Requests',
  cellClassName: () => 'spell-col',
  renderCell: (params: RenderCellParams) => getRequests(params),
};
export const objectives: GridColDef = {
  field: 'objectives',
  description:
    'Objectives set using OPs planner and shows the target value on the enemy province',
  headerName: 'Objectives',
  valueGetter: (params: ValueGetterParams) => getObjectives(params),
};
export const badSpells: GridColDef = {
  field: 'badSpells',
  headerName: 'BadSpells',
  cellClassName: () => 'spell-col',
  valueGetter: (params: ValueGetterParams) => getBadShortSpellNames(params),
  renderCell: (params: RenderCellParams) => getBadSpells(params),
};
export const goodSpells: GridColDef = {
  field: 'goodSpells',
  headerName: 'GoodSpells',
  cellClassName: () => 'spell-col',
  valueGetter: (params: ValueGetterParams) => getGoodShortSpellNames(params),
  renderCell: (params: RenderCellParams) => getGoodSpells(params),
};

export const trainCredits: GridColDef = {
  field: 'creditsTrain',
  headerName: 'T-Credits',
  align: 'right',
  headerAlign: 'left',
  valueGetter: (params: ValueGetterParams) => params.row.ma?.credits,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.ma?.credits,
      params.row.som?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const buildCredits: GridColDef = {
  field: 'creditsBuild',
  headerName: 'B-Credits',
  align: 'right',
  headerAlign: 'left',
  valueGetter: (params: ValueGetterParams) => params.row.survey?.credits,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.survey?.credits,
      params.row.survey?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const gcpa: GridColDef = {
  field: 'gcpa',
  headerName: 'GCpa',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.gcpa ?? null,
  renderCell: (params: RenderCellParams) =>
    colorCodedResult(params.row.sot?.gcpa ?? null, params.row.sot?.ageSeconds),
};
export const gcs: GridColDef = {
  field: 'gcs',
  headerName: 'GCs',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.money,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.money,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const food: GridColDef = {
  field: 'food',
  headerName: 'Food',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.food,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.food,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const runes: GridColDef = {
  field: 'runes',
  headerName: 'Runes',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.runes,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(
      params.row.sot?.runes,
      params.row.sot?.ageSeconds
    ),
  sortComparator: simpleComparator,
};
export const tb: GridColDef = {
  field: 'tb',
  headerName: 'TB',
  align: 'right',
  headerAlign: 'left',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row.sot?.tb,
  renderCell: (params: RenderCellParams) =>
    colorCodedLetterizedResult(params.row.sot?.tb, params.row.sot?.ageSeconds),
  sortComparator: simpleComparator,
};

export const getSciences = (sciences: string[]) => {
  const scienceArr: GridColDef[] = [
    {
      field: 'stockedBooks',
      headerName: 'Stocked',
      align: 'right',
      headerAlign: 'left',
      type: 'number',
      valueGetter: (params: ValueGetterParams) =>
        params.row?.sos?.unallocatedBooks,
      renderCell: (params: RenderCellParams) =>
        colorCodedLetterizedResult(
          params.row?.sos?.unallocatedBooks,
          params.row?.sos?.ageSeconds
        ),
    },
    {
      field: 'allocatedBooks',
      headerName: 'Allocated',
      align: 'right',
      headerAlign: 'left',
      type: 'number',
      valueGetter: (params: ValueGetterParams) =>
        params.row?.sos?.allocatedBooks,
      renderCell: (params: RenderCellParams) =>
        colorCodedLetterizedResult(
          params.row?.sos?.allocatedBooks,
          params.row?.sos?.ageSeconds
        ),
    },
  ];
  sciences.forEach((s, i) => {
    scienceArr.push({
      field: `boo*${i}`,
      headerName: s.substring(0, 5),
      align: 'right',
      headerAlign: 'left',
      type: 'number',
      valueGetter: (params: ValueGetterParams) => getSciValue(params, s),
      renderCell: (params: RenderCellParams) =>
        colorCodedResult(
          getSciPercentage(params, s),
          params.row.sos?.ageSeconds
        ),
      sortComparator: simpleComparator,
    });
  });
  scienceArr.push({
    field: 'sosAge',
    headerName: 'Sos',
    type: 'number',
    valueGetter: (params: ValueGetterParams) =>
      params.row?.sos?.ageSeconds ?? 0,
    renderCell: (params: RenderCellParams) =>
      getIntelAgeWithToolTip(params.row?.sos?.ageSeconds, 'SOS'),
    sortComparator: simpleComparator,
  });
  return scienceArr;
};

export const getBuildings = (builds: string[]) => {
  const buildsArr: GridColDef[] = [];
  builds.forEach((s, i) => {
    buildsArr.push({
      field: `bld*${i}`,
      headerName: getBuildingColName(s),
      align: 'right',
      headerAlign: 'left',
      type: 'number',
      valueGetter: (params: ValueGetterParams) => getSurveyValue(params, s),
      renderCell: (params: RenderCellParams) =>
        colorCodedResult(
          getSurveyRenderValue(params, s),
          params.row.survey?.ageSeconds
        ),
      sortComparator: simpleComparator,
    });
  });
  buildsArr.push({
    field: 'surveyAge',
    headerName: 'Sur',
    type: 'number',
    valueGetter: (params: ValueGetterParams) =>
      params.row?.survey?.ageSeconds ?? 0,
    renderCell: (params: RenderCellParams) =>
      getIntelAgeWithToolTip(params.row?.survey?.ageSeconds, 'Survey'),
    sortComparator: simpleComparator,
  });
  return buildsArr;
};

export const intelAgeCols: GridColDef = {
  field: 'intelAge',
  headerName: 'IntelAge',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row?.sot?.ageSeconds,
  renderCell: (params: RenderCellParams) => getAllIntelAge(params),
  sortComparator: simpleComparator,
};

export const sotAge: GridColDef = {
  field: 'sotAge',
  headerName: 'Sot',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row?.sot?.ageSeconds ?? 0,
  renderCell: (params: RenderCellParams) =>
    getIntelAgeWithToolTip(params.row?.sot?.ageSeconds, 'SOT'),
  sortComparator: simpleComparator,
};

export const somAge: GridColDef = {
  field: 'somAge',
  headerName: 'Som',
  type: 'number',
  valueGetter: (params: ValueGetterParams) => params.row?.som?.ageSeconds ?? 0,
  renderCell: (params: RenderCellParams) =>
    getIntelAgeWithToolTip(params.row?.som?.ageSeconds, 'SOM'),
  sortComparator: simpleComparator,
};

export const infilAge: GridColDef = {
  field: 'infilAge',
  headerName: 'Infil',
  type: 'number',
  valueGetter: (params: ValueGetterParams) =>
    params.row?.sot?.infilAgeSeconds ?? 0,
  renderCell: (params: RenderCellParams) =>
    getIntelAgeWithToolTip(params.row?.sot?.infilAgeSeconds, 'Infiltrate'),
  sortComparator: simpleComparator,
};
